import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import {
  Search,
  X,
  CreditCard,
  TrendingUp,
  TrendingDown,
  RefreshCcw,
  CheckCircle2,
  HelpCircle,
  AlertTriangle,
  Eye,
  XCircle,
  FileText,
  ArrowLeftRight as ObTransactionsLogoIcon
} from 'lucide-react';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import TableSortLabel from '@mui/material/TableSortLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useJob } from '../context/JobContext';

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

const StatWidget = ({ title, value, icon: Icon, color, onClick, active }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      borderColor: active ? color : 'rgba(145, 158, 171, 0.24)'
    }}
    elevation={0}
    variant="outlined"
    onClick={onClick}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function OpenBankingTransactions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const startOfYearIso = `${today.getFullYear()}-01-01`;

  const formatDisplayDate = (iso) => {
    if (!iso || typeof iso !== 'string') return '';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const [yyyy, mm, dd] = parts;
    return `${dd}/${mm}/${yyyy}`;
  };

  const [filters, setFilters] = useState({
    fiscal_id: '',
    account_uuid: '',
    from_date: startOfYearIso,
    to_date: todayIso,
    stato_movimento: 'all'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [orderBy, setOrderBy] = useState('made_on');
  const [order, setOrder] = useState('desc');
  const [accounts, setAccounts] = useState([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncSummary, setSyncSummary] = useState([]);
  const [syncType, setSyncType] = useState('all');
  const [syncAccountUuid, setSyncAccountUuid] = useState('');
  const [syncMode, setSyncMode] = useState('incremental');
  const [syncFromDate, setSyncFromDate] = useState('');
  const [syncToDate, setSyncToDate] = useState('');
  const { addJob } = useJob();
  const [searchDescription, setSearchDescription] = useState('');
  const [amountFilter, setAmountFilter] = useState('all');
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [manageRow, setManageRow] = useState(null);
  const [manageIgnore, setManageIgnore] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [summaryCredit, setSummaryCredit] = useState(0);
  const [summaryDebit, setSummaryDebit] = useState(0);
  const [statementDialogOpen, setStatementDialogOpen] = useState(false);
  const [statementAccounts, setStatementAccounts] = useState([]);
  const [statementFromDate, setStatementFromDate] = useState(startOfYearIso);
  const [statementToDate, setStatementToDate] = useState(todayIso);
  const [statementAmountFilter, setStatementAmountFilter] = useState('all');
  const [statementStatoMovimento, setStatementStatoMovimento] = useState('all');
  const [statementLoading, setStatementLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState('gestito');

  const fetchData = async (options = {}) => {
    setLoading(true);
    try {
      const { nextPage, nextRowsPerPage, nextOrderBy, nextOrder, nextFilters } =
        options;

      const effectiveFilters = nextFilters || filters;
      const params = buildFilterParams(effectiveFilters);

      const pageToUse =
        typeof nextPage === 'number' && nextPage >= 0 ? nextPage : page;
      const limitToUse =
        typeof nextRowsPerPage === 'number' && nextRowsPerPage > 0
          ? nextRowsPerPage
          : rowsPerPage;
      const orderByToUse = nextOrderBy || orderBy;
      const orderToUse = nextOrder || order;

      params.page = pageToUse + 1;
      params.limit = limitToUse;
      params.orderBy = orderByToUse;
      params.orderDirection = orderToUse;

      const res = await api.get('/open-banking/transactions', { params });
      const data = res.data || {};

      if (Array.isArray(data)) {
        setRows(data);
        setTotalItems(data.length);
        setSummaryCredit(0);
        setSummaryDebit(0);
      } else {
        setRows(Array.isArray(data.data) ? data.data : []);
        setTotalItems(data.total || 0);
        setSummaryCredit(data.totalCredit || 0);
        setSummaryDebit(data.totalDebit || 0);
      }
    } catch (error) {
      console.error('Error fetching transactions', error);
      toast.error('Errore nel caricamento delle transazioni');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Caricamento iniziale con filtri di default (da inizio anno a oggi)
    fetchData({ nextFilters: filters, nextPage: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const res = await api.get('/open-banking/accounts');
        setAccounts(res.data || []);
      } catch (error) {
        console.error('Error fetching accounts for transactions', error);
      } finally {
        setAccountsLoaded(true);
      }
    };
    loadAccounts();
  }, []);

  const buildFilterParams = (sourceFilters) => {
    const params = {};
    const accountUuid = (sourceFilters.account_uuid || '').trim();
    const from = (sourceFilters.from_date || '').trim();
    const to = (sourceFilters.to_date || '').trim();
     const statoMovimento = (sourceFilters.stato_movimento || '').trim();

    if (accountUuid) params.account_uuid = accountUuid;
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    if (statoMovimento && statoMovimento !== 'all') {
      params.stato_movimento = statoMovimento;
    }
    if (searchDescription.trim()) params.search = searchDescription.trim();

    if (amountFilter && amountFilter !== 'all') {
      params.amount_filter = amountFilter;
    }

    return params;
  };

  const applyFilters = (nextFilters) => {
    setPage(0);
    setFilters(nextFilters);
    fetchData({ nextFilters: nextFilters, nextPage: 0 });
  };

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    const next = { ...filters, [field]: value };
    setFilters(next);
  };

  const handleFilterBlur = () => {
    applyFilters(filters);
  };

  const clearField = (field) => {
    const next = { ...filters, [field]: field === 'from_date' || field === 'to_date' ? '' : '' };
    setFilters(next);
    applyFilters(next);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData({ nextPage: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
    fetchData({ nextPage: 0, nextRowsPerPage: value });
  };

  const visibleRows = useMemo(() => rows, [rows]);
  const visibleRowIds = useMemo(
    () => visibleRows.map((row) => row.id).filter((id) => id != null),
    [visibleRows]
  );
  const allVisibleSelected =
    visibleRowIds.length > 0 && visibleRowIds.every((id) => selectedRows.includes(id));

  const formatCurrency = (amount, currency) => {
    if (amount == null) return '-';
    try {
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(amount);
    } catch (e) {
      return amount;
    }
  };

  const amountColor = (amount) => {
    if (amount == null) return undefined;
    if (amount > 0) return 'success.main';
    if (amount < 0) return 'error.main';
    return 'text.primary';
  };

  const getStatoTooltip = (row) => {
    const raw = row.stato_movimento;
    if (!raw || raw === 'da_gestire') {
      return 'Da gestire';
    }
    const base =
      raw === 'gestito'
        ? 'Gestito'
        : raw === 'non_gestire'
        ? 'Non gestire'
        : 'Stato sconosciuto';

    const when = row.stato_movimento_updated_at
      ? new Date(row.stato_movimento_updated_at).toLocaleString('it-IT')
      : null;
    const who =
      row.stato_movimento_updated_by_name ||
      (row.stato_movimento_updated_by != null
        ? `Utente #${row.stato_movimento_updated_by}`
        : null);

    if (who && when) return `${base} da ${who} il ${when}`;
    if (who) return `${base} da ${who}`;
    if (when) return `${base} il ${when}`;
    return base;
  };

  const renderExtraData = (value) => {
    if (!value) return null;
    try {
      const text = typeof value === 'string' ? value : JSON.stringify(value);
      const parsed = JSON.parse(text);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      if (typeof value === 'string') return value;
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
  };

  const totalTransactions = totalItems;
  const totalCredit = summaryCredit;
  const totalDebit = summaryDebit;

  const accountsMap = useMemo(() => {
    const map = {};
    accounts.forEach((acc) => {
      map[acc.uuid] = acc;
    });
    return map;
  }, [accounts]);

  const renderAccountOption = (acc) => {
    const isEnabled = acc.enabled;
    const color = isEnabled ? '#16a34a' : '#dc2626';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18
          }}
        >
          {isEnabled ? (
            <CheckCircle2 size={16} color={color} />
          ) : (
            <XCircle size={16} color={color} />
          )}
        </Box>
        <Typography variant="body2">
          {formatAccountLabel(acc.uuid)}
        </Typography>
      </Box>
    );
  };

  const formatAccountLabel = (uuid) => {
    if (!uuid) return 'Tutti gli account';
    const acc = accountsMap[uuid];
    if (!acc) return uuid;
    const parts = [];
    if (acc.business_name) parts.push(acc.business_name);
    if (acc.provider_name) parts.push(acc.provider_name);
    if (parts.length === 0) return uuid;
    return parts.join(' · ');
  };

  const renderAccountCell = (row) => {
    const acc = accountsMap[row.account_uuid];
    if (!acc) {
      return (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {row.account_uuid || '-'}
        </Typography>
      );
    }

    const businessName = acc.business_name || null;
    const rawName = acc.name || '';
    const looksLikeIban =
      typeof rawName === 'string' &&
      /^IT[0-9A-Z]{10,}$/i.test(rawName.trim());
    const displayName = acc.provider_name || (!looksLikeIban ? rawName : row.account_uuid);
    const iban =
      acc.iban ||
      acc.account_number ||
      (looksLikeIban ? rawName.trim() : '');
    const isEnabled = acc.enabled;
    const statusLabel = isEnabled ? 'Attivo' : 'Disabilitato';
    const statusColor = isEnabled ? '#16a34a' : '#f97316';

    return (
      <Box>
        {businessName && (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {businessName}
          </Typography>
        )}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mt: businessName ? 0.25 : 0 }}
        >
          {displayName}
        </Typography>
        {iban && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {iban}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: statusColor
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {statusLabel}
          </Typography>
        </Box>
      </Box>
    );
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const nextOrder = isAsc ? 'desc' : 'asc';
    setOrder(nextOrder);
    setOrderBy(property);
    setPage(0);
    fetchData({
      nextPage: 0,
      nextOrderBy: property,
      nextOrder
    });
  };

  useEffect(() => {
    setPage(0);
    fetchData({ nextPage: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountFilter]);

  useEffect(() => {
    setSelectedRows((prev) => prev.filter((id) => visibleRowIds.includes(id)));
  }, [visibleRowIds]);

  const openSyncDialog = async () => {
    try {
      setSyncDialogOpen(true);
      setSyncLoading(true);
      const params = {};
      if (syncType === 'account' && syncAccountUuid) {
        params.account_uuid = syncAccountUuid;
      }
      const res = await api.get('/open-banking/transactions/sync-summary', { params });
      setSyncSummary(res.data || []);
    } catch (error) {
      console.error('Error fetching sync summary', error);
      toast.error('Errore nel recupero del riepilogo movimenti');
    } finally {
      setSyncLoading(false);
    }
  };

  const openManageDialog = (row) => {
    setManageRow(row);
    setManageIgnore(false);
    setManageDialogOpen(true);
  };

  const openStatementDialog = () => {
    setStatementAccounts(filters.account_uuid ? [filters.account_uuid] : []);
    setStatementFromDate(filters.from_date || startOfYearIso);
    setStatementToDate(filters.to_date || todayIso);
    setStatementAmountFilter(amountFilter || 'all');
    setStatementStatoMovimento(filters.stato_movimento || 'all');
    setStatementDialogOpen(true);
  };

  const handleStatementConfirm = async () => {
    try {
      setStatementLoading(true);
      const payload = {
        accountUuids: Array.isArray(statementAccounts)
          ? statementAccounts.filter(Boolean)
          : [],
        fromDate: statementFromDate,
        toDate: statementToDate,
        amountFilter: statementAmountFilter || 'all',
        stato_movimento: statementStatoMovimento || 'all'
      };
      const res = await api.post('/open-banking/transactions/statement', payload);
      const jobId = res.data && res.data.jobId;
      if (jobId) {
        addJob(jobId, 'open_banking_statement');
      }
      toast.success(
        res.data?.message || 'Generazione estratto conto banche avviata'
      );
      setStatementDialogOpen(false);
    } catch (error) {
      console.error('Error starting open banking statement report', error);
      toast.error('Errore durante l\'avvio dell\'estratto conto banche');
    } finally {
      setStatementLoading(false);
    }
  };

  const openDetailDialog = (row) => {
    setDetailRow(row);
    setDetailDialogOpen(true);
  };

  const handleConfirmManage = async () => {
    if (!manageRow) {
      setManageDialogOpen(false);
      return;
    }
    const newState = manageIgnore ? 'non_gestire' : 'gestito';
    try {
      const res = await api.patch(
        `/open-banking/transactions/${manageRow.id}/stato-movimento`,
        { stato_movimento: newState }
      );
      toast.success(res.data?.message || 'Movimento aggiornato');
      setManageDialogOpen(false);
      setManageRow(null);
      // aggiorna riga localmente
      setRows((prev) =>
        prev.map((r) =>
          r.id === manageRow.id ? { ...r, stato_movimento: newState } : r
        )
      );
    } catch (error) {
      console.error('Error updating stato_movimento', error);
      toast.error('Errore durante l\'aggiornamento del movimento');
    }
  };

  const handleSyncConfirm = async () => {
    try {
      setSyncLoading(true);
      const payload = {
        type: syncType === 'account' ? 'account' : 'all',
        account_uuid: syncType === 'account' ? syncAccountUuid : undefined,
        from_date: syncMode === 'period' && syncFromDate ? syncFromDate : undefined,
        to_date: syncMode === 'period' && syncToDate ? syncToDate : undefined
      };
      const res = await api.post('/open-banking/transactions/sync-recent', payload);
      const ids =
        (res.data && Array.isArray(res.data.jobIds) && res.data.jobIds.length > 0
          ? res.data.jobIds
          : res.data && res.data.jobId
          ? [res.data.jobId]
          : []);

      if (ids.length > 0) {
        ids.forEach((id) => addJob(id, 'open_banking_tx_sync'));
      } else {
        toast.success('Sincronizzazione avviata');
      }
      setSyncDialogOpen(false);
    } catch (error) {
      console.error('Error syncing recent transactions', error);
      toast.error('Errore durante la sincronizzazione dei movimenti');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleToggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedRows((prev) => prev.filter((id) => !visibleRowIds.includes(id)));
      return;
    }
    setSelectedRows((prev) => {
      const merged = new Set([...prev, ...visibleRowIds]);
      return Array.from(merged);
    });
  };

  const handleSelectAllFilteredTransactions = async () => {
    if (totalItems > 0 && selectedRows.length >= totalItems) {
      setSelectedRows([]);
      return;
    }

    try {
      const params = buildFilterParams(filters);
      params.page = 1;
      params.limit = totalItems || 10000;
      params.orderBy = orderBy;
      params.orderDirection = order;

      const res = await api.get('/open-banking/transactions', { params });
      const data = res.data || {};
      const allRows = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
      const allIds = allRows.map((item) => item.id).filter((id) => id != null);

      setSelectedRows(allIds);
      toast.success(`Selezionati ${allIds.length} movimenti (in base ai filtri correnti)`);
    } catch (error) {
      console.error('Error selecting all filtered transactions', error);
      toast.error('Errore nel selezionare tutti i movimenti filtrati');
    }
  };

  const handleBulkUpdateStatoMovimento = async (nextStatoMovimento) => {
    if (!Array.isArray(selectedRows) || selectedRows.length === 0) {
      toast.error('Seleziona almeno un movimento');
      return false;
    }

    try {
      const res = await api.patch('/open-banking/transactions/bulk/stato-movimento', {
        ids: selectedRows,
        stato_movimento: nextStatoMovimento
      });

      toast.success(res?.data?.message || `Aggiornati ${selectedRows.length} movimenti`);
      setSelectedRows([]);
      fetchData({ nextPage: page });
      return true;
    } catch (error) {
      console.error('Error bulk update stato_movimento', error);
      toast.error(
        error?.response?.data?.message ||
          "Errore durante l'aggiornamento massivo dei movimenti"
      );
      return false;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ObTransactionsLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Transazioni
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Movimenti bancari importati via Open Banking
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileText size={18} />}
            onClick={openStatementDialog}
          >
            Estratto conto
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshCcw size={18} />}
            onClick={openSyncDialog}
          >
            Sincronizza
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <StatWidget
            title="Totale transazioni"
            value={totalTransactions.toLocaleString('it-IT')}
            icon={CreditCard}
            color="#0ea5e9"
            onClick={() => setAmountFilter('all')}
            active={amountFilter === 'all'}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatWidget
            title="Accrediti"
            value={formatCurrency(totalCredit, 'EUR')}
            icon={TrendingUp}
            color="#16a34a"
            onClick={() => setAmountFilter('credit')}
            active={amountFilter === 'credit'}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatWidget
            title="Addebiti"
            value={formatCurrency(totalDebit, 'EUR')}
            icon={TrendingDown}
            color="#dc2626"
            onClick={() => setAmountFilter('debit')}
            active={amountFilter === 'debit'}
          />
        </Grid>
      </Grid>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Account</InputLabel>
                <Select
                  label="Account"
                  value={filters.account_uuid}
                  onChange={(event) => {
                    const value = event.target.value;
                    const next = { ...filters, account_uuid: value };
                    setFilters(next);
                    applyFilters(next);
                  }}
                  disabled={!accountsLoaded}
                >
                  <MenuItem value="">
                    <em>Tutti gli account</em>
                  </MenuItem>
                  {accounts.map((acc) => (
                    <MenuItem key={acc.uuid} value={acc.uuid}>
                      {renderAccountOption(acc)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Descrizione"
                value={searchDescription}
                onChange={(e) => setSearchDescription(e.target.value)}
                onBlur={() => applyFilters(filters)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} color="#637381" />
                    </InputAdornment>
                  ),
                  endAdornment: searchDescription ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchDescription('');
                          applyFilters(filters);
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Stato</InputLabel>
                <Select
                  label="Stato"
                  value={filters.stato_movimento}
                  onChange={(event) => {
                    const value = event.target.value;
                    const next = { ...filters, stato_movimento: value };
                    setFilters(next);
                    applyFilters(next);
                  }}
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  <MenuItem value="da_gestire">Da gestire</MenuItem>
                  <MenuItem value="gestito">Gestito</MenuItem>
                  <MenuItem value="non_gestire">Non gestire</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Data da"
                type="date"
                value={filters.from_date}
                onChange={(event) => {
                  const value = event.target.value;
                  const next = { ...filters, from_date: value };
                  setFilters(next);
                  applyFilters(next);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Data a"
                type="date"
                value={filters.to_date}
                onChange={(event) => {
                  const value = event.target.value;
                  const next = { ...filters, to_date: value };
                  setFilters(next);
                  applyFilters(next);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Filtri attivi
            </Typography>
            {filters.account_uuid && (
              <Chip
                size="small"
                label={`Account: ${formatAccountLabel(filters.account_uuid)}`}
                onDelete={() => clearField('account_uuid')}
              />
            )}
            {amountFilter === 'credit' && (
              <Chip
                size="small"
                label="Solo accrediti"
                onDelete={() => setAmountFilter('all')}
              />
            )}
            {amountFilter === 'debit' && (
              <Chip
                size="small"
                label="Solo addebiti"
                onDelete={() => setAmountFilter('all')}
              />
            )}
            {filters.stato_movimento && filters.stato_movimento !== 'all' && (
              <Chip
                size="small"
                label={
                  filters.stato_movimento === 'da_gestire'
                    ? 'Da gestire'
                    : filters.stato_movimento === 'gestito'
                    ? 'Gestito'
                    : 'Non gestire'
                }
                onDelete={() => {
                  const next = { ...filters, stato_movimento: 'all' };
                  setFilters(next);
                  applyFilters(next);
                }}
              />
            )}
            {filters.from_date && (
              <Chip
                size="small"
                label={`Da: ${formatDisplayDate(filters.from_date)}`}
                onDelete={() => clearField('from_date')}
              />
            )}
            {filters.to_date && (
              <Chip
                size="small"
                label={`A: ${formatDisplayDate(filters.to_date)}`}
                onDelete={() => clearField('to_date')}
              />
            )}
            {!filters.account_uuid && !filters.from_date && !filters.to_date && amountFilter === 'all' && (
              <Typography variant="caption" color="text.secondary">
                Nessun filtro applicato
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {selectedRows.length > 0
                ? `Selezionati ${selectedRows.length} movimenti`
                : 'Nessun movimento selezionato'}
              {totalItems > 0 && ` (totale ${totalItems} con i filtri correnti)`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {selectedRows.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<DoneAllIcon fontSize="small" />}
                  onClick={() => {
                    setBulkStatusValue('gestito');
                    setBulkStatusDialogOpen(true);
                  }}
                >
                  Cambia stato
                </Button>
              )}
              {totalItems > 0 && (
                <Tooltip
                  title={
                    selectedRows.length >= totalItems
                      ? 'Deseleziona tutti i movimenti filtrati'
                      : 'Seleziona tutti i movimenti filtrati'
                  }
                >
                  <IconButton
                    color="primary"
                    onClick={handleSelectAllFilteredTransactions}
                    sx={{
                      bgcolor: 'primary.50',
                      border: '1px solid',
                      borderColor: 'primary.200',
                      '&:hover': { bgcolor: 'primary.100' }
                    }}
                  >
                    <DoneAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={allVisibleSelected}
                    indeterminate={selectedRows.length > 0 && !allVisibleSelected}
                    onChange={handleToggleSelectAllVisible}
                  />
                </TableCell>
                <TableCell align="center">Da gestire</TableCell>
                <TableCell sortDirection={orderBy === 'account' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'account'}
                    direction={orderBy === 'account' ? order : 'asc'}
                    onClick={() => handleRequestSort('account')}
                  >
                    Account
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'made_on' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'made_on'}
                    direction={orderBy === 'made_on' ? order : 'asc'}
                    onClick={() => handleRequestSort('made_on')}
                  >
                    Data
                  </TableSortLabel>
                </TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Ordinante</TableCell>
                <TableCell align="right" sortDirection={orderBy === 'amount' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'amount'}
                    direction={orderBy === 'amount' ? order : 'asc'}
                    onClick={() => handleRequestSort('amount')}
                  >
                    Importo
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'type' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'type'}
                    direction={orderBy === 'type' ? order : 'asc'}
                    onClick={() => handleRequestSort('type')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Dettaglio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nessuna transazione trovata.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleToggleRowSelection(row.id)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 32
                        }}
                      >
                        {row.stato_movimento === 'gestito' ? (
                          <Tooltip title={getStatoTooltip(row)} arrow>
                            <CheckCircle2 size={20} color="#22c55e" />
                          </Tooltip>
                        ) : row.stato_movimento === 'non_gestire' ? (
                          <Tooltip title={getStatoTooltip(row)} arrow>
                            <XCircle size={20} color="#dc2626" />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Gestisci movimento" arrow>
                            <IconButton
                              size="small"
                              onClick={() => openManageDialog(row)}
                            >
                              <HelpCircle size={20} color="#dc2626" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {renderAccountCell(row)}
                    </TableCell>
                    <TableCell>
                      {row.made_on
                        ? new Date(row.made_on).toLocaleDateString('it-IT')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.description}</Typography>
                      {row.additional && (
                        <Typography variant="caption" color="text.secondary">
                          {row.additional}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.merchant_name || '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', color: amountColor(row.amount) }}
                      >
                        {formatCurrency(row.amount, row.currency_code)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={Number(row.amount || 0) >= 0 ? 'Accredito' : 'Addebito'}
                        size="small"
                        color={Number(row.amount || 0) >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => openDetailDialog(row)}
                        title="Dettaglio transazione"
                      >
                        <Eye size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[30, 60, 90]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog
        open={bulkStatusDialogOpen}
        onClose={() => setBulkStatusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cambia stato movimenti selezionati</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scegli il nuovo stato da applicare a {selectedRows.length} movimenti selezionati.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="bulk-status-ob-label">Nuovo stato</InputLabel>
            <Select
              labelId="bulk-status-ob-label"
              label="Nuovo stato"
              value={bulkStatusValue}
              onChange={(event) => setBulkStatusValue(event.target.value)}
            >
              <MenuItem value="da_gestire">Da gestire</MenuItem>
              <MenuItem value="gestito">Segna come gestiti</MenuItem>
              <MenuItem value="non_gestire">Non gestire</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkStatusDialogOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const ok = await handleBulkUpdateStatoMovimento(bulkStatusValue);
              if (ok) setBulkStatusDialogOpen(false);
            }}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={syncDialogOpen}
        onClose={() => !syncLoading && setSyncDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Sincronizza Recenti</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Tipo di Sincronizzazione
            </Typography>
            <RadioGroup
              row
              value={syncType}
              onChange={(e) => setSyncType(e.target.value)}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label="Tutti gli account abilitati"
              />
              <FormControlLabel
                value="account"
                control={<Radio />}
                label="Account specifico"
              />
            </RadioGroup>
            {syncType === 'account' && (
              <Box sx={{ mt: 2, maxWidth: 400 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Account</InputLabel>
                  <Select
                    label="Account"
                    value={syncAccountUuid}
                    onChange={(e) => setSyncAccountUuid(e.target.value)}
                    disabled={!accountsLoaded}
                  >
                    {accounts.map((acc) => (
                      <MenuItem key={acc.uuid} value={acc.uuid}>
                        {renderAccountOption(acc)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Periodo di sincronizzazione
            </Typography>
            <RadioGroup
              row
              value={syncMode}
              onChange={(e) => setSyncMode(e.target.value)}
            >
              <FormControlLabel
                value="incremental"
                control={<Radio />}
                label="Da ultimo movimento (incrementale)"
              />
              <FormControlLabel
                value="period"
                control={<Radio />}
                label="Periodo specifico"
              />
            </RadioGroup>
            {syncMode === 'period' && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2, maxWidth: 420 }}>
                <TextField
                  label="Da data"
                  type="date"
                  value={syncFromDate}
                  onChange={(e) => setSyncFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="A data"
                  type="date"
                  value={syncToDate}
                  onChange={(e) => setSyncToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                />
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              Riepilogo ultimi movimenti
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ultima data transazione per ogni account abilitato
            </Typography>
          </Box>

          {syncLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>UUID</TableCell>
                    <TableCell>Ultimo movimento</TableCell>
                    <TableCell>Ragione sociale</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncSummary.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Nessun account trovato.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    syncSummary.map((row, idx) => {
                      const name = row.name || '';
                      const looksLikeIban =
                        typeof name === 'string' &&
                        /^IT[0-9A-Z]{10,}$/i.test(name.trim());
                      const accountIdentifier =
                        row.iban ||
                        row.account_number ||
                        (looksLikeIban ? name.trim() : '') ||
                        row.uuid ||
                        '-';

                      return (
                        <TableRow key={idx}>
                          <TableCell>{row.provider_name || '-'}</TableCell>
                          <TableCell>{accountIdentifier}</TableCell>
                          <TableCell>{row.uuid}</TableCell>
                          <TableCell>
                            {row.last_movement
                              ? new Date(row.last_movement).toLocaleDateString('it-IT')
                              : 'N/D'}
                          </TableCell>
                          <TableCell>{row.business_name || '-'}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSyncDialogOpen(false)}
            disabled={syncLoading}
          >
            Annulla
          </Button>
          <Button
            onClick={handleSyncConfirm}
            variant="contained"
            disabled={syncLoading || (syncType === 'account' && !syncAccountUuid)}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={statementDialogOpen}
        onClose={() => !statementLoading && setStatementDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Estratto conto banche</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Seleziona i conti, il periodo e il tipo di movimenti da includere
              nell&apos;estratto conto. Se non selezioni alcun conto, verranno
              considerati tutti gli account abilitati.
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Conti</InputLabel>
              <Select
                multiple
                label="Conti"
                value={statementAccounts}
                onChange={(e) => setStatementAccounts(e.target.value)}
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return 'Tutti gli account';
                  }
                  if (selected.length === 1) {
                    return formatAccountLabel(selected[0]);
                  }
                  return `${selected.length} account selezionati`;
                }}
              >
                {accounts.map((acc) => (
                  <MenuItem key={acc.uuid} value={acc.uuid}>
                    <Checkbox
                      size="small"
                      checked={statementAccounts.indexOf(acc.uuid) > -1}
                    />
                    {renderAccountOption(acc)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Stato</InputLabel>
              <Select
                label="Stato"
                value={statementStatoMovimento}
                onChange={(e) => setStatementStatoMovimento(e.target.value)}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="da_gestire">Da gestire</MenuItem>
                <MenuItem value="gestito">Gestito</MenuItem>
                <MenuItem value="non_gestire">Non gestire</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                label="Data da"
                type="date"
                value={statementFromDate}
                onChange={(e) => setStatementFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                fullWidth
                label="Data a"
                type="date"
                value={statementToDate}
                onChange={(e) => setStatementToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Tipo di movimenti
              </Typography>
              <RadioGroup
                row
                value={statementAmountFilter}
                onChange={(e) => setStatementAmountFilter(e.target.value)}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label="Accrediti e addebiti"
                />
                <FormControlLabel
                  value="credit"
                  control={<Radio />}
                  label="Solo accrediti"
                />
                <FormControlLabel
                  value="debit"
                  control={<Radio />}
                  label="Solo addebiti"
                />
              </RadioGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setStatementDialogOpen(false)}
            disabled={statementLoading}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleStatementConfirm}
            disabled={statementLoading}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AlertTriangle size={20} color="#f59e0b" />
            </Box>
            <Typography variant="h6">Gestisci movimento</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {manageRow && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}
              >
                Transazione
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
                  rowGap: 0.75,
                  columnGap: 3
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Conto
                </Typography>
                <Typography variant="body2">
                  {formatAccountLabel(manageRow.account_uuid)}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Tipo
                </Typography>
                <Typography variant="body2">
                  {Number(manageRow.amount || 0) >= 0 ? 'Accredito' : 'Addebito'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Importo
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(manageRow.amount, manageRow.currency_code)}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Data
                </Typography>
                <Typography variant="body2">
                  {manageRow.made_on
                    ? new Date(manageRow.made_on).toLocaleDateString('it-IT')
                    : '-'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Ordinante / Controparte
                </Typography>
                <Typography variant="body2">
                  {manageRow.merchant_name || '-'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Descrizione
                </Typography>
                <Typography variant="body2">
                  {manageRow.description || '-'}
                </Typography>

                {manageRow.additional && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Ulteriori dettagli
                    </Typography>
                    <Typography variant="body2">{manageRow.additional}</Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Vuoi contrassegnare questo movimento come gestito?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Questa azione serve per tracciare internamente se il movimento è stato preso in carico
              o non richiede ulteriori attività.
            </Typography>
          </Box>
          <RadioGroup
            row
            value={manageIgnore ? 'non_gestire' : 'gestito'}
            onChange={(e) => setManageIgnore(e.target.value === 'non_gestire')}
            sx={{ mt: 1, columnGap: 4 }}
          >
            <FormControlLabel
              value="gestito"
              control={<Radio color="primary" />}
              label="Sì, il movimento è stato gestito"
            />
            <FormControlLabel
              value="non_gestire"
              control={<Radio color="warning" />}
              label="Il movimento non è da gestire"
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleConfirmManage}>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Dettaglio transazione</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Informazioni generali
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
                    rowGap: 0.75,
                    columnGap: 3,
                    mt: 1
                  }}
                >
                  <Typography color="text.secondary">ID transazione</Typography>
                  <Typography>{detailRow.transaction_id || '-'}</Typography>
                  <Typography color="text.secondary">Conto</Typography>
                  <Typography>{formatAccountLabel(detailRow.account_uuid)}</Typography>
                  <Typography color="text.secondary">Azienda</Typography>
                  <Typography>{detailRow.fiscal_id || '-'}</Typography>
                  <Typography color="text.secondary">Data movimento</Typography>
                  <Typography>
                    {detailRow.made_on
                      ? new Date(detailRow.made_on).toLocaleDateString('it-IT')
                      : '-'}
                  </Typography>
                  <Typography color="text.secondary">Importo</Typography>
                  <Typography>
                    {formatCurrency(detailRow.amount, detailRow.currency_code)}
                  </Typography>
                  <Typography color="text.secondary">Valuta</Typography>
                  <Typography>{detailRow.currency_code || '-'}</Typography>
                  <Typography color="text.secondary">Tipo</Typography>
                  <Typography>
                    {Number(detailRow.amount || 0) >= 0 ? 'Accredito' : 'Addebito'}
                  </Typography>
                  <Typography color="text.secondary">Stato</Typography>
                  <Typography>{detailRow.status || '-'}</Typography>
                  <Typography color="text.secondary">Categoria</Typography>
                  <Typography>{detailRow.category || '-'}</Typography>
                  <Typography color="text.secondary">Duplicato</Typography>
                  <Typography>{detailRow.duplicated ? 'Sì' : 'No'}</Typography>
                  <Typography color="text.secondary">Stato movimento</Typography>
                  <Typography>{detailRow.stato_movimento || '-'}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Descrizione
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    {detailRow.description || '-'}
                  </Typography>
                  {detailRow.additional && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {detailRow.additional}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Controparte
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
                    rowGap: 0.75,
                    columnGap: 3,
                    mt: 1
                  }}
                >
                  <Typography color="text.secondary">Nome</Typography>
                  <Typography>{detailRow.merchant_name || '-'}</Typography>
                  <Typography color="text.secondary">Merchant code</Typography>
                  <Typography>{detailRow.merchant_code || '-'}</Typography>
                  <Typography color="text.secondary">Riferimento</Typography>
                  <Typography>{detailRow.reference || '-'}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Metadati provider
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
                    rowGap: 0.75,
                    columnGap: 3,
                    mt: 1
                  }}
                >
                  <Typography color="text.secondary">Creato (Acube)</Typography>
                  <Typography>
                    {detailRow.acube_created_at
                      ? new Date(detailRow.acube_created_at).toLocaleString('it-IT')
                      : '-'}
                  </Typography>
                  <Typography color="text.secondary">Aggiornato (Acube)</Typography>
                  <Typography>
                    {detailRow.acube_updated_at
                      ? new Date(detailRow.acube_updated_at).toLocaleString('it-IT')
                      : '-'}
                  </Typography>
                </Box>
              </Box>
              {detailRow.extra_data && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Dati aggiuntivi (JSON)
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: '#f9fafb',
                      fontFamily: 'monospace',
                      fontSize: 12,
                      maxHeight: 240,
                      overflow: 'auto'
                    }}
                  >
                    {renderExtraData(detailRow.extra_data)}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
